import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext, DragEndEvent, DragOverlay, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';

import { cn } from '@/lib/utils';
import { CrmStage, bySort, reorderWithin } from '../../../common/models/crm.model';

type BoardItem = { _id: string; stage: string; sort: number; due?: string };

interface CrmBoardProps<T extends BoardItem> {
  stages: CrmStage[];
  /** Already filtered; the board groups them by stage. */
  items: T[];
  /** Keep the manual order (true) or the order `items` arrive in (another sort). */
  manualOrder: boolean;
  renderCard: (item: T) => React.ReactNode;
  /** Optional right-aligned figure in a column header (e.g. its total value). */
  columnExtra?: (items: T[]) => React.ReactNode;
  onOpen: (item: T) => void;
  /** Card dropped on a column: change its stage. */
  onMoveStage: (id: string, stage: string) => void;
  /** Card dropped on another card: the column's new order (dragged card included, with its new stage). */
  onReorder: (updates: { id: string; sort: number; stage?: string }[], stage: string) => void;
}

const DraggableCard: React.FC<{ id: string; onOpen: () => void; children: React.ReactNode }> = ({ id, onOpen, children }) => {
  const drag = useDraggable({ id });
  const drop = useDroppable({ id: `card:${id}` });
  return (
    <div
      ref={(node) => { drag.setNodeRef(node); drop.setNodeRef(node); }}
      {...drag.listeners}
      {...drag.attributes}
      onClick={onOpen}
      className={cn(
        'cursor-grab rounded-xl border bg-card p-3 transition-colors select-none hover:bg-accent/40 active:cursor-grabbing',
        drag.isDragging && 'opacity-35',
        drop.isOver && !drag.isDragging && 'border-foreground',
      )}
    >
      {children}
    </div>
  );
};

function Column<T extends BoardItem>({ stage, items, extra, children }: {
  stage: CrmStage; items: T[]; extra?: React.ReactNode; children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: `col:${stage.id}` });
  return (
    <div
      ref={setNodeRef}
      data-testid={`crm-column-${stage.id}`}
      className={cn('flex w-64 shrink-0 flex-col border p-2.5 transition-colors', isOver ? 'border-foreground bg-accent' : 'bg-muted')}
    >
      <div className="flex items-center gap-2 px-1 pb-2.5">
        <span className="size-2 rounded-full" style={{ background: stage.color }} />
        <span className="text-sm font-medium">{t(stage.label)}</span>
        {extra && <span className="text-xs text-muted-foreground">{extra}</span>}
        <span className="ms-auto text-sm text-muted-foreground">{items.length}</span>
      </div>
      <div className="flex max-h-[calc(100vh-340px)] min-h-24 flex-1 flex-col gap-2 overflow-y-auto">{children}</div>
    </div>
  );
}

/**
 * Kanban for CRM leads and partners. Drop a card on a column to change its
 * stage; drop it on another card to slot it there (manual order, persisted).
 */
export function CrmBoard<T extends BoardItem>({
  stages, items, manualOrder, renderCard, columnExtra, onOpen, onMoveStage, onReorder,
}: CrmBoardProps<T>) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const byStage = (stageId: string) => {
    const col = items.filter((i) => i.stage === stageId);
    return manualOrder ? [...col].sort(bySort) : col;
  };
  const active = items.find((i) => i._id === activeId);

  const handleDragEnd = ({ active: a, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over) return;
    const draggedId = String(a.id);
    const overId = String(over.id);
    const dragged = items.find((i) => i._id === draggedId);
    if (!dragged) return;

    if (overId.startsWith('card:')) {
      const target = items.find((i) => i._id === overId.slice(5));
      if (!target || target._id === draggedId) return;
      // The target's column, with the dragged card in it, in manual order.
      const column = items.filter((i) => i.stage === target.stage || i._id === draggedId).sort(bySort);
      const updates = reorderWithin(column, draggedId, target._id)
        .map((u) => (u.id === draggedId && dragged.stage !== target.stage ? { ...u, stage: target.stage } : u));
      if (updates.length) onReorder(updates, target.stage);
      return;
    }
    if (overId.startsWith('col:')) {
      const stage = overId.slice(4);
      if (stage !== dragged.stage) onMoveStage(draggedId, stage);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={(e) => setActiveId(String(e.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div className="flex items-start gap-3 overflow-x-auto pb-3">
        {stages.map((st) => {
          const col = byStage(st.id);
          return (
            <Column key={st.id} stage={st} items={col} extra={columnExtra?.(col)}>
              {col.map((item) => (
                <DraggableCard key={item._id} id={item._id} onOpen={() => onOpen(item)}>{renderCard(item)}</DraggableCard>
              ))}
              {col.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{t('ADMIN_CRM.COMMON.EMPTY')}</p>}
            </Column>
          );
        })}
      </div>
      <DragOverlay>
        {active ? <div className="w-60 cursor-grabbing rounded-xl border bg-card p-3 shadow-md">{renderCard(active)}</div> : null}
      </DragOverlay>
    </DndContext>
  );
}
